import React from 'react';

const SpcAcademy2026Page: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-100/30 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto text-center space-y-12">
          <div className="space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-relaxed">
              スピリアルコンシェルジュアカデミー
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 font-light leading-relaxed">
              ― 表舞台に光をともす、Light Workerという新たなキャリア ―
            </p>
          </div>

          <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/cdafb07856154aa36d9b75f52ff78fba.jpg"
              alt="Light Worker"
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
            <p>表に立つ人がいます。</p>
            <p>メッセージを届け、<br />人の人生に光を灯す人。</p>
            <p className="pt-6">けれど、<br />その光がまっすぐ届くためには、<br />もう一つのLight Workerの役割があります。</p>
          </div>
        </div>
      </section>

      {/* Two Types of Light Workers */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
            光を放つ人と、光を通す人
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border border-amber-200 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-sun-line text-5xl text-amber-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">表舞台で光を放つ人</h3>
              <p className="text-gray-700 leading-relaxed">
                メッセージを届け、人々の心に直接光を灯す存在
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-10 rounded-2xl border border-gray-200 shadow-lg">
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-5xl text-gray-700"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">光を通す人</h3>
              <p className="text-gray-700 leading-relaxed">
                その光が、曇らず、歪まず、必要な人のもとへ届くように整える存在
              </p>
            </div>
          </div>

          <div className="text-center space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
            <p className="font-bold text-2xl text-amber-800">
              スピリアルコンシェルジュアカデミーが育てるのは、<br />後者のLight Workerです。
            </p>
          </div>
        </div>
      </section>

      {/* Role Description */}
      <section className="py-20 px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
            <p className="font-bold text-2xl text-gray-900">場を整えることで、光を通す</p>
            <p className="font-bold text-2xl text-gray-900">流れをつくることで、メッセージを届ける</p>
            <p className="font-bold text-2xl text-gray-900">人が安心して輝ける舞台をひらく</p>
          </div>

          <div className="pt-8 space-y-4">
            <p className="text-xl md:text-2xl text-gray-800 leading-relaxed">
              それは裏方ではありません。
            </p>
            <p className="text-2xl md:text-3xl font-bold text-amber-800">
              光の通路をつくる仕事です。
            </p>
          </div>
        </div>
      </section>

      {/* Dynavision History */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            ダイナビジョンが30年以上続いてきた理由
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
              <p>
                ダイナビジョンが<br />
                30年以上にわたり、<br />
                多くの人の人生に光を届け続けてきたのは、
              </p>
              <p className="font-bold text-xl">
                ただ強いメッセージがあったからではありません。
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border border-amber-200 shadow-lg">
              <ul className="space-y-4 text-xl text-gray-800">
                <li className="flex items-center">
                  <i className="ri-time-line text-2xl text-amber-600 mr-4 w-8 h-8 flex items-center justify-center"></i>
                  <span>時間</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-layout-line text-2xl text-amber-600 mr-4 w-8 h-8 flex items-center justify-center"></i>
                  <span>空間</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-list-check text-2xl text-amber-600 mr-4 w-8 h-8 flex items-center justify-center"></i>
                  <span>段取り</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-heart-line text-2xl text-amber-600 mr-4 w-8 h-8 flex items-center justify-center"></i>
                  <span>安心感</span>
                </li>
                <li className="flex items-center">
                  <i className="ri-shield-check-line text-2xl text-amber-600 mr-4 w-8 h-8 flex items-center justify-center"></i>
                  <span>信頼</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
            <p>
              これらすべてが整ったとき、<br />
              光は初めて"届く力"を持ちます。
            </p>
            <p className="font-bold text-2xl text-amber-800">
              その調律を担ってきた存在こそが、<br />
              光を通すLight Workerでした。
            </p>
          </div>
        </div>
      </section>

      {/* Transition Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            受け取る側から、光をともす側へ
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p>
              これまであなたは、<br />
              ダイナビジョンの世界を<br />
              「光を受け取る側」として体験してきたかもしれません。
            </p>

            <p className="text-xl font-bold text-gray-900">
              けれど今、<br />
              もし心のどこかでこんな感覚があるなら。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12">
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800">表に出るより、場を整える方がしっくりくる</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800">人が本来の輝きを放つ瞬間に立ち会いたい</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800">スピリチュアルを、現実の仕事として体現したい</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800">光が届く"仕組み"をつくる側に回りたい</p>
              </div>
            </div>

            <p className="text-xl md:text-2xl font-bold text-center text-amber-800 pt-6">
              それは、<br />
              あなた自身がLight Workerとして次の段階に入ったサインです。
            </p>
          </div>
        </div>
      </section>

      {/* Academy Description */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            スピリアルコンシェルジュアカデミーとは
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed mb-12">
            <p className="text-xl text-center">
              スピリアルコンシェルジュアカデミーは、<br />
              ダイナビジョンの現場で<br />
              光を通す役割を担うLight Workerを育てるアカデミーです。
            </p>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border border-amber-200 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">ここで学ぶのは</h3>
            <ul className="space-y-4 text-lg text-gray-800">
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <span>講師と光をつなぐ調整力</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <span>セミナーやLIVEという「場」の設計</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <span>事務局としての在り方</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <span>スケジュールとエネルギーの同時管理</span>
              </li>
              <li className="flex items-start">
                <i className="ri-checkbox-circle-line text-2xl text-amber-600 mr-4 mt-1 w-8 h-8 flex items-center justify-center"></i>
                <span>イレギュラー時に光を止めない判断力</span>
              </li>
            </ul>
          </div>

          <div className="mt-12 text-center space-y-4">
            <p className="text-xl text-gray-700">これは単なる運営スキルではありません。</p>
            <p className="text-2xl md:text-3xl font-bold text-amber-800">光を現実に届けるための技術です。</p>
          </div>
        </div>
      </section>

      {/* Honest Message */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            正直にお伝えします
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl text-center">
              この役割は、<br />
              軽く、楽なものではありません。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12">
              <div className="bg-white p-8 rounded-xl border-2 border-gray-300 shadow-sm">
                <p className="text-gray-800">急な変更</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-gray-300 shadow-sm">
                <p className="text-gray-800">責任ある判断</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-gray-300 shadow-sm">
                <p className="text-gray-800">人と人の間に立つ緊張感</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-gray-300 shadow-sm">
                <p className="text-gray-800">目立たない役割</p>
              </div>
            </div>

            <div className="text-center space-y-6 pt-8">
              <p className="text-xl font-bold text-gray-900">それでも──</p>
              <p className="text-2xl md:text-3xl font-bold text-amber-800">
                「光が届いた瞬間」を知ってしまった人は、<br />
                この役割から離れられなくなります。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Academy */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            なぜ、アカデミーという形なのか
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl text-center font-bold text-gray-900">
              Light Workerは、<br />
              名乗るものではなく、育まれるものです。
            </p>

            <p className="text-center">
              だからダイナビジョンは、<br />
              いきなり任せるのではなく、<br />
              アカデミーという形で育てることを選びました。
            </p>

            <div className="bg-gradient-to-br from-amber-50 to-white p-10 rounded-2xl border border-amber-200 shadow-lg mt-12">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">まずは説明会で</h3>
              <ul className="space-y-4 text-lg text-gray-800">
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-2xl text-amber-600 mr-3 mt-1 w-8 h-8 flex items-center justify-center"></i>
                  <span>この役割のリアル</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-2xl text-amber-600 mr-3 mt-1 w-8 h-8 flex items-center justify-center"></i>
                  <span>大変さ</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-2xl text-amber-600 mr-3 mt-1 w-8 h-8 flex items-center justify-center"></i>
                  <span>それでも選ぶ意味</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-s-line text-2xl text-amber-600 mr-3 mt-1 w-8 h-8 flex items-center justify-center"></i>
                  <span>向いている人・向いていない人</span>
                </li>
              </ul>
              <p className="text-center mt-6 font-bold text-gray-900">すべてを正直にお伝えします。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Future Path */}
      <section className="py-20 px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            この先にひらかれる道
          </h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl text-center font-bold text-gray-900">アカデミー修了後、</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm text-center">
                <i className="ri-building-line text-4xl text-amber-600 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-800 font-bold">ダイナビジョン事務局としての実務参加</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm text-center">
                <i className="ri-briefcase-line text-4xl text-amber-600 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-800 font-bold">アルバイト・業務委託・正社員候補</p>
              </div>
              <div className="bg-white p-8 rounded-xl border-2 border-amber-200 shadow-sm text-center">
                <i className="ri-team-line text-4xl text-amber-600 mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
                <p className="text-gray-800 font-bold">継続的なプロジェクト参加</p>
              </div>
            </div>

            <p className="text-xl text-center pt-8">
              など、<br />
              Light Workerとして現実社会で光をともす道がひらかれます。
            </p>
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center space-y-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">最後に</h2>

          <div className="space-y-8 text-lg text-gray-700 leading-relaxed">
            <p className="text-xl">もしあなたが、</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800 font-bold">光を放つ人を支えたい</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800 font-bold">表舞台に光をともす仕事がしたい</p>
              </div>
              <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl border-2 border-amber-200 shadow-sm">
                <p className="text-gray-800 font-bold">スピリチュアルを現実に届けたい</p>
              </div>
            </div>

            <p className="text-xl pt-6">そう感じているなら。</p>

            <p className="text-2xl md:text-3xl font-bold text-amber-800">
              スピリアルコンシェルジュアカデミーは、<br />
              あなたのための入口です。
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
            まずは、説明会でお会いしましょう。
          </h2>

          <a 
            href="#"
            className="inline-block bg-amber-600 hover:bg-amber-700 text-white font-bold text-lg px-12 py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            説明会に申し込む
          </a>

          <p className="text-sm text-gray-600 italic mt-6">
            ※説明会の詳細は、お申し込み後にメールでご案内いたします。
          </p>
        </div>
      </section>
    </div>
  );
};

export default SpcAcademy2026Page;
